import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FEATURE_FLAG_EVENT_NAME,
  FEATURE_FLAG_STORAGE_KEY,
  type FeatureFlagName,
  type FeatureFlagState,
  broadcastFeatureFlagUpdate,
  mergeFeatureFlags,
  persistFeatureFlags,
  readFeatureFlagsFromStorage,
  resolveFeatureFlags,
} from "../utils/featureFlags";

interface FeatureFlagContextValue {
  flags: FeatureFlagState;
  setFlag: (flagName: FeatureFlagName, value: boolean) => void;
  refreshFlags: () => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

export interface FeatureFlagProviderProps {
  children: React.ReactNode;
  initialFlags?: Partial<FeatureFlagState>;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
  initialFlags,
}) => {
  const [flags, setFlags] = useState<FeatureFlagState>(() =>
    mergeFeatureFlags(resolveFeatureFlags(), initialFlags)
  );

  const persistAndBroadcast = useCallback((next: FeatureFlagState) => {
    persistFeatureFlags(next);
    broadcastFeatureFlagUpdate(next);
  }, []);

  const setFlag = useCallback(
    (flagName: FeatureFlagName, value: boolean) => {
      setFlags((prev) => {
        if (prev[flagName] === value) {
          return prev;
        }

        const next = { ...prev, [flagName]: value } as FeatureFlagState;
        persistAndBroadcast(next);
        return next;
      });
    },
    [persistAndBroadcast]
  );

  const refreshFlags = useCallback(() => {
    setFlags((prev) => mergeFeatureFlags(prev, resolveFeatureFlags()));
  }, []);

  useEffect(() => {
    if (!initialFlags) {
      return;
    }

    setFlags((prev) => mergeFeatureFlags(prev, initialFlags));
  }, [initialFlags]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === FEATURE_FLAG_STORAGE_KEY) {
        setFlags((prev) => mergeFeatureFlags(prev, readFeatureFlagsFromStorage()));
      }
    };

    const handleBroadcast = (event: Event) => {
      const customEvent = event as CustomEvent<FeatureFlagState>;
      if (customEvent.detail) {
        setFlags((prev) => mergeFeatureFlags(prev, customEvent.detail));
      }
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(
      FEATURE_FLAG_EVENT_NAME,
      handleBroadcast as EventListener
    );

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(
        FEATURE_FLAG_EVENT_NAME,
        handleBroadcast as EventListener
      );
    };
  }, []);

  const value = useMemo(
    () => ({
      flags,
      setFlag,
      refreshFlags,
    }),
    [flags, refreshFlags, setFlag]
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlagContext = (): FeatureFlagContextValue => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlagContext must be used within a FeatureFlagProvider");
  }
  return context;
};

export default FeatureFlagContext;
